import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { createTaskForMeeting } from "../../features/tasks/taskSlice";
import { toast } from "react-toastify";
import { Icons } from "../../constants/icon-library";
import { getIcon } from "../../constants/constants";
import { getParticipantSecondary } from "./Meetings.utils";

const AddTaskModal = ({
  show,
  onHide,
  meeting,
  task,
  settings,
  participants,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .required("Task name is required")
      .min(3, "Task name must be at least 3 characters"),
    description: Yup.string()
      .trim()
      .required("Description is required")

      .min(10, "Description must be at least 10 characters"),
    settingId: Yup.string().required("Platform is required"),
    email: Yup.string().required("Assignee is required"),
  });

  const initialValues = {
    name: "",
    description: "",
    settingId: "",
    email: "",
  };

  const handleSubmit = (values, { resetForm }) => {
    try {
      setLoading(true);
      dispatch(
        createTaskForMeeting({
          meetingData: values,
          meetingId: meeting?.uuid,
          taskId: task?.uuid,
        }),
      );
      resetForm();
      if (loading === false) {
        onHide();
      }
    } catch (err) {
      toast.error(`${err.message || "Failed to create task"}`);
      console.error("Failed to create task:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block add-task-modal-overlay"
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-centered modal-md">
        <div className="modal-content">
          {/* HEADER */}
          <div className="modal-header border-secondary d-flex justify-content-between align-items-center">
            <div>
              <h5 className="modal-title">Add New Task</h5>
            </div>
            <div>
              <button
                type="button"
                className="btn-close ms-auto"
                onClick={onHide}
                aria-label="Close"
              />
            </div>
          </div>

          {/* BODY */}
          <div className="modal-body">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, values, setFieldValue }) => (
                <Form>
                  <div className="mb-4">
                    <label
                      htmlFor="taskName"
                      className="form-label fw-bold mb-2 add-task-label"
                    >
                      <i className={Icons("edit")} /> Task Name
                    </label>
                    <Field
                      type="text"
                      className="form-control  rounded-3"
                      id="taskName"
                      name="name"
                      placeholder="Enter task name"
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-danger fs-9 mt-2 fw-semibold add-task-error"
                    />
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="description"
                      className="form-label fw-bold mb-2 add-task-label"
                    >
                      <i className={Icons("paragraph")} /> Description
                    </label>
                    <Field
                      as="textarea"
                      className="form-control  rounded-3"
                      id="description"
                      name="description"
                      rows="4"
                      placeholder="Enter task description"
                    />
                    <ErrorMessage
                      name="description"
                      component="div"
                      className="text-danger fs-9 mt-2 fw-semibold add-task-error"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold mb-2 add-task-label">
                      Platform
                    </label>

                    <div className="dropdown">
                      <button
                        className="btn btn-phoenix-secondary w-100 text-start d-flex justify-content-between align-items-center"
                        type="button"
                        data-bs-toggle="dropdown"
                      >
                        <span>
                          {values.settingId
                            ? settings.find((s) => s.uuid === values.settingId)
                                ?.assistantName ||
                              settings.find((s) => s.uuid === values.settingId)
                                ?.assistantId?.name
                            : "Select Platform"}
                        </span>
                        <span className="caret" />
                      </button>

                      <div className="dropdown-menu w-100 py-0">
                        {settings.length === 0 ? (
                          <span className="dropdown-item text-muted small">
                            No settings available
                          </span>
                        ) : (
                          settings.map((setting) => {
                            const label =
                              setting.assistantName ||
                              setting.assistantId?.name ||
                              "Unknown";

                            const icon =
                              getIcon(setting.assistantId?.platform?.type) ||
                              setting.assistantId?.platform?.iconUrl;

                            return (
                              <button
                                key={setting.uuid}
                                type="button"
                                className={`dropdown-item d-flex align-items-center gap-2 ${
                                  values.settingId === setting.uuid
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() =>
                                  setFieldValue("settingId", setting.uuid)
                                }
                              >
                                {icon && (
                                  <img
                                    src={icon}
                                    alt={label}
                                    width={16}
                                    height={16}
                                  />
                                )}
                                <span className="small">{label}</span>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>

                    <ErrorMessage
                      name="settingId"
                      component="div"
                      className="text-danger fs-9 mt-2 fw-semibold add-task-error"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold mb-2 add-task-label">
                      <i className={Icons("user")} /> Assignee
                    </label>

                    <div className="dropdown">
                      <button
                        className="btn btn-phoenix-secondary w-100 text-start d-flex justify-content-between align-items-center"
                        type="button"
                        data-bs-toggle="dropdown"
                      >
                        <span>
                          {values.email
                            ? (() => {
                                const p = participants.find(
                                  (p) =>
                                    (p.email || p.username) === values.email,
                                );
                                if (!p) return values.email;

                                return `${p.name} (${getParticipantSecondary(p)})`;
                              })()
                            : "Select Assignee"}
                        </span>
                        <span className="caret" />
                      </button>

                      <div
                        className="dropdown-menu w-100 py-0"
                        style={{ maxHeight: "200px", overflowY: "auto" }}
                      >
                        {!participants || participants.length === 0 ? (
                          <span className="dropdown-item text-muted small">
                            No participants available
                          </span>
                        ) : (
                          participants.map((participant) => {
                            const identifier =
                              participant.email || participant.username;

                            return (
                              <button
                                key={participant.uuid}
                                type="button"
                                className={`dropdown-item d-flex align-items-center gap-2 ${
                                  values.email === identifier ? "active" : ""
                                }`}
                                onClick={() =>
                                  setFieldValue("email", identifier)
                                }
                              >
                                <span className="small fw-semibold">
                                  {participant.name}{" "}
                                  <span className="text-muted">
                                    ({getParticipantSecondary(participant)})
                                  </span>
                                </span>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>

                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-danger fs-9 mt-2 fw-semibold add-task-error"
                    />
                  </div>

                  <div className="modal-footer border-secondary px-0 d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm d-flex align-items-center gap-2"
                      onClick={onHide}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                      disabled={loading}
                    >
                      Add Task
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTaskModal;
